import { UserApi } from 'api/user/user-api.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { DietDb } from 'lib/cosmos/diet/dietDb.js';
import { FoodDb } from 'lib/cosmos/food/foodDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { WorkoutDb } from 'lib/cosmos/workout/workoutDb.js';
import { AppError } from 'lib/error/error.models.js';
import { isNotUndefined } from 'lib/helpers/array.utils.js';
import { nameHelper } from 'lib/helpers/name-helper.js';
import { pluralHelper } from 'lib/helpers/plural.helper.class.js';
import { unitConversionHelper } from 'lib/helpers/unit-conversion-helper.class.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { map, of } from 'rxjs';
import { ChatConversationComponent } from 'src/app/modules/chat/chat-conversation.component.js';
import { ClientOverviewComponent } from 'src/app/modules/users/client-overview.component.js';
import { EditClientPackagesComponent } from 'src/app/modules/users/edit-client-packages.component.js';
import { EditClientComponent } from 'src/app/modules/users/edit-client.component.js';
import { ListClientsComponent } from 'src/app/modules/users/list-clients.component.js';
import { NewClientComponent } from 'src/app/modules/users/new-client.component.js';
import { match, P } from 'ts-pattern';
import { CoreAppComponentDependencies } from '../../core/core-app-dependencies.js';
import { ViewOrEditDietComponent } from '../../modules/diets/view-or-edit-diet.component.js';
import { foodHelper } from '../../modules/foods/food.helper.js';
import { ClientDiaryComponent } from '../../modules/users/diary/client-diary.component.js';
import { EditUserPasswordComponent } from '../../modules/users/edit-user-password.component.js';
import { ClientFilesComponent } from '../../modules/users/files/client-files.component.js';
import { ClientGalleryComponent } from '../../modules/users/gallery/client-gallery.component.js';
import { ClientDietComponent } from '../../modules/users/nutrition/client-diet.component.js';
import { ClientProgressComponent } from '../../modules/users/progress/client-progress.component.js';
import { ClientSessionsComponent } from '../../modules/users/sessions/client-sessions.component.js';
import { ClientTasksComponent } from '../../modules/users/tasks/client-tasks.component.js';
import { ClientTrainingComponent } from '../../modules/users/training/client-training.component.js';
import { ViewOrEditWorkoutComponent } from '../../modules/workouts/view-or-edit-workout.component.js';
import { WorkoutFormComponent } from '../../modules/workouts/workout-form.component.js';
import {
	ICoreRoutes,
	IPageMetadata,
	IPageTag,
	ISharedData,
	ITypePageCard,
	ITypePageCardAvatar,
	ITypeParentRoute,
	ITypeRoute,
	ITypeRouteAction,
	RouteInitializer
} from '../../pages/type-routes/type-route.models.js';
import { routeParams } from '../../routeParams.js';
import { IViewOrEditDietPlanRouteData } from './diet-routes.js';
import { IViewOrEditWorkoutPlanRouteData } from './workout-routes.js';

export interface ITrainerClientRouteData {
	client: UserDb.IUserItem;
}

export interface ITrainerClientRoutes extends ICoreRoutes {
	clients: ITypeRoute<
		undefined,
		{
			new: ITypeRouteAction<undefined>;
			list: ITypeRouteAction<undefined>;
		}
	>;
	client: ITypeRoute<
		UserDb.IUserItem,
		{
			view: ITypeRouteAction<UserDb.IUserItem>;
			training: ITypeRouteAction<UserDb.IUserItem>;
			nutrition: ITypeRouteAction<UserDb.IUserItem>;
			progress: ITypeRouteAction<UserDb.IUserItem>;
			tasks: ITypeRouteAction<UserDb.IUserItem>;
			chat: ITypeRouteAction<UserDb.IUserItem>;
			files: ITypeRouteAction<UserDb.IUserItem>;
			gallery: ITypeRouteAction<UserDb.IUserItem>;
			diary: ITypeRouteAction<UserDb.IUserItem>;
			sessions: ITypeRouteAction<UserDb.IUserItem>;
			packages: ITypeRouteAction<UserDb.IUserItem>;
			profile: ITypeRouteAction<UserDb.IUserItem>;
			password: ITypeRouteAction<UserDb.IUserItem>;
		}
	>;
	'client-workout': ITypeRoute<
		IViewOrEditWorkoutPlanRouteData,
		{
			view: ITypeRouteAction<IViewOrEditWorkoutPlanRouteData>;
		}
	>;
	'client-activity': ITypeRoute<
		IViewOrEditWorkoutPlanRouteData,
		{
			edit: ITypeRouteAction<IViewOrEditWorkoutPlanRouteData>;
		}
	>;
	'client-diet': ITypeRoute<
		IViewOrEditDietPlanRouteData,
		{
			view: ITypeRouteAction<IViewOrEditDietPlanRouteData>;
		}
	>;
}

export const trainerClientRoutes: RouteInitializer<ITrainerClientRoutes> = {
	clients: {
		mainPath: 'trainer',
		uniqueId: 'trainerClients',
		parentRoutes: (context) => [],
		loadSharedData: (context) => {
			return of(undefined);
		},
		menuItems: (context) => [],
		children: {
			list: {
				uniqueId: `clientsOverview`,
				component: ListClientsComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: 'Clients'
					};
					return metadata;
				}
			},
			new: {
				uniqueId: `newClient`,
				parentRoutes: (context) => [
					{
						routerLink: context.dependencies.navigateService
							.actionRoute((routes) => routes.trainerClientRoutes.clients.children.list)
							.getUrl(),
						title: `Clients`
					}
				],
				component: NewClientComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: 'New'
					};
					return metadata;
				}
			}
		}
	},
	client: {
		mainPath: 'trainer',
		uniqueId: 'trainerClient',
		parentRoutes: (context) => {
			const routes: ITypeParentRoute[] = [];

			return [
				{
					routerLink: context.dependencies.navigateService
						.actionRoute((routes) => routes.trainerClientRoutes.clients.children.list)
						.getUrl(),
					title: `Clients`
				},
				...routes
			];
		},
		loadSharedData: (context) => {
			return context.dependencies.httpService
				.get<UserDb.IUserItem>((builder) =>
					builder
						.withCustomApiRoute('user', 'getUser')
						.withId(context.routeData.primaryId)
						.withQueryString(routeParams.loadPackages, true) // needed on client overview
						.build()
				)
				.pipe(
					map((response) => {
						const shared: ISharedData<UserDb.IUserItem> = {
							data: response.data,
							linkedItems: response.linkedItems ?? []
						};

						return shared;
					})
				);
		},
		menuItems: (context) => [
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.view,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Dashboard`,
				iconType: 'dashboard'
			},
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.chat,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Chat`,
				iconType: 'email'
			},
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.tasks,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Tasks`,
				iconType: 'task'
			},
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.training,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Training`,
				iconType: 'workout'
			},
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.progress,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Progress`,
				iconType: 'analytics'
			},
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.nutrition,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Nutrition`,
				iconType: 'diet'
			},

			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.files,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Files`,
				iconType: 'files'
			},
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.gallery,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Gallery`,
				iconType: 'gallery'
			},
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.diary,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Diary`,
				iconType: 'text'
			},
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.sessions,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Sessions`,
				iconType: 'date'
			},
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.packages,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Packages`,
				iconType: 'purchase'
			},
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.password,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Password`,
				iconType: 'password'
			},
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes.client.children.profile,
						context.routeData.primaryId
					)
					.getUrl(),
				title: `Profile`,
				iconType: 'user_pen'
			}
		],
		children: {
			view: {
				uniqueId: `viewClient`,
				component: ClientOverviewComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Dashboard`,
						card: getUserCard(context.sharedData?.data, context.dependencies, {
							linkedItems: []
						})
					};
					return metadata;
				}
			},
			chat: {
				uniqueId: `clientChat`,
				component: ChatConversationComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Chat`,
						card: getUserCard(context.sharedData?.data, context.dependencies, {
							linkedItems: []
						})
					};
					return metadata;
				}
			},
			packages: {
				uniqueId: `editClientPackages`,
				component: EditClientPackagesComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Packages`,
						card: getUserCard(context.sharedData?.data, context.dependencies, {
							linkedItems: []
						})
					};
					return metadata;
				}
			},
			training: {
				uniqueId: `trainerClientTraining`,
				component: ClientTrainingComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Training`,
						card: getUserCard(context.sharedData?.data, context.dependencies, {
							linkedItems: [],
							extraButtons: context.sharedData?.data
								? [
										{
											title: 'Download plan PDF',
											click: () => {
												const user = context.sharedData?.data;

												if (user) {
													context.dependencies.dialogService.openPdfDialog({
														generatePdf: () =>
															context.dependencies.httpService.generateTrainingPlanPdf({
																user: user
															})
													});
												}
											}
										}
								  ]
								: []
						})
					};
					return metadata;
				}
			},
			nutrition: {
				uniqueId: `trainerClientNutrition`,
				component: ClientDietComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Nutrition`,
						card: getUserCard(context.sharedData?.data, context.dependencies, {
							linkedItems: [],
							extraButtons: context.sharedData?.data
								? [
										{
											title: 'Download plan PDF',
											click: () => {
												const user = context.sharedData?.data;

												if (user) {
													context.dependencies.dialogService.openPdfDialog({
														generatePdf: () =>
															context.dependencies.httpService.generateDietPlanPdf({
																user: user
															})
													});
												}
											}
										}
								  ]
								: []
						})
					};
					return metadata;
				}
			},
			progress: {
				uniqueId: `trainerClientProgress`,
				component: ClientProgressComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Progress`,
						card: getUserCard(context.sharedData?.data, context.dependencies, {
							linkedItems: []
						})
					};
					return metadata;
				}
			},
			files: {
				uniqueId: `trainerClientFiles`,
				component: ClientFilesComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Files`,
						card: getUserCard(context.sharedData?.data, context.dependencies, {
							linkedItems: []
						})
					};
					return metadata;
				}
			},
			gallery: {
				uniqueId: `trainerClientGallery`,
				component: ClientGalleryComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Gallery`,
						card: getUserCard(context.sharedData?.data, context.dependencies, {
							linkedItems: []
						})
					};
					return metadata;
				}
			},
			tasks: {
				uniqueId: `trainerClientTasks`,
				component: ClientTasksComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Tasks`,
						card: getUserCard(context.sharedData?.data, context.dependencies, {
							linkedItems: []
						})
					};
					return metadata;
				}
			},
			diary: {
				uniqueId: `trainerClientDiary`,
				component: ClientDiaryComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Diary`,
						card: getUserCard(context.sharedData?.data, context.dependencies, {
							linkedItems: []
						})
					};
					return metadata;
				}
			},
			sessions: {
				uniqueId: `trainerClientSessions`,
				component: ClientSessionsComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Sessions`,
						card: getUserCard(context.sharedData?.data, context.dependencies, {
							linkedItems: []
						})
					};
					return metadata;
				}
			},
			profile: {
				uniqueId: `editClient`,
				component: EditClientComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Edit profile`,
						card: getUserCard(context.sharedData?.data, context.dependencies, {
							linkedItems: []
						})
					};
					return metadata;
				}
			},

			password: {
				uniqueId: `editClientPassword`,
				component: EditUserPasswordComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Password`,
						card: getUserCard(context.sharedData?.data, context.dependencies, {
							linkedItems: []
						})
					};
					return metadata;
				}
			}
		}
	},
	'client-workout': {
		mainPath: 'trainer',
		uniqueId: 'trainerClientWorkout',
		parentRoutes: (context) => {
			const clientRoutes: ITypeParentRoute[] = [];
			const workoutRoutes: ITypeParentRoute[] = [];

			return [
				{
					routerLink: context.dependencies.navigateService
						.actionRoute((routes) => routes.trainerClientRoutes.clients.children.list)
						.getUrl(),
					title: `Clients`
				},
				...clientRoutes,
				{
					routerLink: context.dependencies.navigateService
						.actionRoute(
							(routes) => routes.trainerClientRoutes.client.children.training,
							context.routeData.primaryId
						)
						.getUrl(),
					title: `Training`
				},
				...workoutRoutes
			];
		},
		loadSharedData: (context) => {
			return context.dependencies.httpService
				.get<UserApi.IUserWithWorkoutResponse>((builder) =>
					builder
						.withCustomApiRoute('user', 'getUserWithWorkout')
						.withPath(
							`/${context.routeData.primaryId}/${context.routeData.secondaryId}/${
								context.routeData.tertiaryId
							}/${context.dependencies.timeService.toISODate(context.dependencies.timeService.now())}`
						)
						.build()
				)
				.pipe(
					map((response) => {
						const container = response.data.user.training?.containers?.find(
							(m) => m.id.toLowerCase() === context.routeData.secondaryId?.toLowerCase()
						);

						if (!container) {
							throw new AppError({
								behavior: 'redirectTo404',
								message: `[getUserWithWorkout] Invalid container with id '${context.routeData.secondaryId}'`
							});
						}

						const containerItem = container.items.find(
							(m) => m.id.toLowerCase() === context.routeData.tertiaryId?.toLowerCase()
						);

						if (!containerItem) {
							throw new AppError({
								behavior: 'redirectTo404',
								message: `[getUserWithWorkout] Invalid container item with id '${context.routeData.tertiaryId}'`
							});
						}

						const shared: ISharedData<IViewOrEditWorkoutPlanRouteData> = {
							data: {
								workout: containerItem.workout,
								sessionForDate: response.data.sessionForDate,
								workoutType: 'workout',
								clientData: {
									client: response.data.user,
									trainingContainer: container,
									trainingItem: containerItem
								}
							},
							linkedItems: response.linkedItems ?? []
						};

						return shared;
					})
				);
		},
		menuItems: (context) => [],
		children: {
			view: {
				uniqueId: `trainerViewClientWorkout`,
				component: ViewOrEditWorkoutComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Preview`,
						card: getUserCard(context.sharedData?.data?.clientData?.client, context.dependencies, {
							linkedItems: context.sharedData?.linkedItems ?? [],
							workoutItem: context.sharedData?.data.clientData?.trainingItem,
							overrideCardSubtitle: context.sharedData?.data?.clientData?.trainingContainer?.title,
							overrideCardTitle: context.sharedData?.data?.clientData?.trainingItem?.workout?.system.name
						})
					};
					return metadata;
				},
				processSharedData: (context) => {
					return context.sharedData;
				}
			}
		}
	},
	'client-activity': {
		mainPath: 'trainer',
		uniqueId: 'clientActivity',
		parentRoutes: (context) => {
			const clientRoutes: ITypeParentRoute[] = [];
			const activityRoutes: ITypeParentRoute[] = [];

			return [
				{
					routerLink: context.dependencies.navigateService
						.actionRoute((routes) => routes.trainerClientRoutes.clients.children.list)
						.getUrl(),
					title: `Clients`
				},
				...clientRoutes,
				{
					routerLink: context.dependencies.navigateService
						.actionRoute(
							(routes) => routes.trainerClientRoutes.client.children.training,
							context.routeData.primaryId
						)
						.getUrl(),
					title: `Training`
				},
				...activityRoutes
			];
		},
		loadSharedData: (context) => {
			return context.dependencies.httpService
				.get<UserApi.IUserWithWorkoutResponse>((builder) =>
					builder
						.withCustomApiRoute('user', 'getUserWithWorkout')
						.withPath(
							`/${context.routeData.primaryId}/${context.routeData.secondaryId}/${
								context.routeData.tertiaryId
							}/${context.dependencies.timeService.toISODate(context.dependencies.timeService.now())}`
						)
						.build()
				)
				.pipe(
					map((response) => {
						const container = response.data.user.training?.containers?.find(
							(m) => m.id.toLowerCase() === context.routeData.secondaryId?.toLowerCase()
						);

						if (!container) {
							throw new AppError({
								behavior: 'redirectTo404',
								message: `[client-activity] Invalid container with id '${context.routeData.secondaryId}'`
							});
						}

						const containerItem = container.items.find(
							(m) => m.id.toLowerCase() === context.routeData.tertiaryId?.toLowerCase()
						);

						if (!containerItem) {
							throw new AppError({
								behavior: 'redirectTo404',
								message: `[client-activity] Invalid container item with id '${context.routeData.tertiaryId}'`
							});
						}

						const shared: ISharedData<IViewOrEditWorkoutPlanRouteData> = {
							data: {
								workout: containerItem.workout,
								sessionForDate: response.data.sessionForDate,
								workoutType: 'workout',
								clientData: {
									client: response.data.user,
									trainingContainer: container,
									trainingItem: containerItem
								}
							},
							linkedItems: response.linkedItems ?? []
						};

						return shared;
					})
				);
		},
		menuItems: (context) => [
			{
				routerLink: context.dependencies.navigateService
					.actionRoute(
						(routes) => routes.trainerClientRoutes['client-activity'].children.edit,
						context.routeData.primaryId,
						context.routeData.secondaryId,
						context.routeData.tertiaryId
					)
					.getUrl(),
				title: `Edit`,
				iconType: 'edit'
			}
		],
		children: {
			edit: {
				uniqueId: `editClientActivity`,
				component: WorkoutFormComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Edit`,
						card: getUserCard(context.sharedData?.data?.clientData?.client, context.dependencies, {
							linkedItems: context.sharedData?.linkedItems ?? [],
							overrideCardSubtitle: context.sharedData?.data?.clientData?.trainingContainer?.title,
							overrideCardTitle: context.sharedData?.data?.clientData?.trainingItem?.workout?.system.name
						})
					};
					return metadata;
				}
			}
		}
	},
	'client-diet': {
		mainPath: 'trainer',
		uniqueId: 'trainerClientDiet',
		parentRoutes: (context) => {
			const clientRoutes: ITypeParentRoute[] = [];
			const dietRoutes: ITypeParentRoute[] = [];

			return [
				{
					routerLink: context.dependencies.navigateService
						.actionRoute((routes) => routes.trainerClientRoutes.clients.children.list)
						.getUrl(),
					title: `Clients`
				},
				...clientRoutes,
				{
					routerLink: context.dependencies.navigateService
						.actionRoute(
							(routes) => routes.trainerClientRoutes.client.children.nutrition,
							context.routeData.primaryId
						)
						.getUrl(),
					title: `Nutrition`
				},
				...dietRoutes
			];
		},
		loadSharedData: (context) => {
			return context.dependencies.httpService
				.get<UserDb.IUserItem>((builder) =>
					builder
						.withCustomApiRoute('user', 'getUser')
						.withId(context.routeData.primaryId)
						.withQueryString(routeParams.loadDietFoods, true)
						.build()
				)
				.pipe(
					map((response) => {
						const container = response.data.nutrition?.containers?.find(
							(m) => m.id.toLowerCase() === context.routeData.secondaryId?.toLowerCase()
						);

						if (!container) {
							throw new AppError({
								behavior: 'redirectTo404',
								message: `[trainerClientDiet] Invalid container with id '${context.routeData.secondaryId}'`
							});
						}

						const containerItem = container.items.find(
							(m) => m.id.toLowerCase() === context.routeData.tertiaryId?.toLowerCase()
						);

						if (!containerItem) {
							throw new AppError({
								behavior: 'redirectTo404',
								message: `[trainerClientDiet] Invalid container item with id '${context.routeData.tertiaryId}'`
							});
						}

						const shared: ISharedData<IViewOrEditDietPlanRouteData> = {
							data: {
								diet: containerItem.diet,
								clientData: {
									client: response.data,
									dietContainer: container,
									dietItem: containerItem
								}
							},
							linkedItems: response.linkedItems ?? []
						};

						return shared;
					})
				);
		},
		menuItems: (context) => [],
		children: {
			view: {
				uniqueId: `trainerViewClientDiet`,
				component: ViewOrEditDietComponent,
				getMetadata: (context) => {
					const metadata: IPageMetadata = {
						title: `Preview`,
						card: getUserCard(context.sharedData?.data?.clientData?.client, context.dependencies, {
							linkedItems: context.sharedData?.linkedItems ?? [],
							dietItem: context.sharedData?.data?.clientData?.dietItem,
							overrideCardSubtitle: context.sharedData?.data?.clientData?.dietContainer?.title,
							overrideCardTitle: context.sharedData?.data?.clientData?.dietItem?.diet?.system.name
						})
					};
					return metadata;
				},
				processSharedData: (context) => {
					return context.sharedData;
				}
			}
		}
	}
};

function getUserCard(
	user: UserDb.IUserItem | undefined,
	dependencies: CoreAppComponentDependencies,
	data: {
		overrideCardSubtitle?: string;
		overrideCardTitle?: string;
		// overrideCardPdf?: ITypeCardPdf;
		linkedItems: CosmosDb.ICosmosItem[];
		dietItem?: UserDb.IUserDietItem;
		workoutItem?: UserDb.IUserTrainingItem;
		extraButtons?: readonly IButton[];
	}
): ITypePageCard | undefined {
	if (!user) {
		return undefined;
	}

	const extraButtons: readonly IButton[] = data.extraButtons ?? [];
	const workout = data.workoutItem?.workout;
	const diet = data.dietItem?.diet;
	const authUser = dependencies.authService.authUser();

	const tags: IPageTag[] = [];

	if (workout) {
		tags.push({
			icon: 'dumbbell',
			text: pluralHelper.getPluralText({
				count: workout.exerciseContainers.length,
				m0: `0 exercises`,
				m1: `1 exercise`,
				m5: `${workout.exerciseContainers.length} exercises`
			})
		});
		if (workout && workout.recommendedLength) {
			tags.push({
				icon: 'length',
				text: workout.recommendedLength
			});
		}
	} else if (diet) {
		tags.push({
			icon: 'food',
			text: pluralHelper.getPluralText({
				count: diet.foodContainers.length,
				m0: `0 meals`,
				m1: `1 meal`,
				m5: `${diet.foodContainers.length} meals`
			})
		});
	} else {
		if (!dependencies.screenService.currentScreen().isMobileView) {
			tags.push({
				icon: 'email',
				text: user.email
			});
		}

		if (user.preferredUnit) {
			if (user.heightCm) {
				tags.push({
					icon: 'height',
					text: unitConversionHelper.heightInPreferredUnit(authUser?.preferredUnit, user.heightCm)
				});
			}

			if (user.weightKg) {
				tags.push({
					icon: 'weight',
					text: unitConversionHelper.weightInPreferredUnit(authUser?.preferredUnit, user.weightKg)
				});
			}
		}

		if (user.timezone) {
			tags.push({
				icon: 'timezone',
				text: `${user.timezone}`
			});
		}
	}

	const username = nameHelper.getUserName(user.email, user.firstName, user.lastName);
	let title: string = username;
	let subtitle: string | undefined = undefined;
	let cardAvatar: ITypePageCardAvatar<any> | undefined;

	if (workout && data.workoutItem) {
		cardAvatar = {
			avatar: workout.avatar?.[0]?.image,
			avatarName: workout.system.name,
			upload: dependencies.coreAppService.getAvatarCardUploadConfig<
				WorkoutDb.IWorkoutItem,
				IViewOrEditWorkoutPlanRouteData
			>({
				type: 'workout',
				existingAvatar: workout?.avatar?.[0],
				patchModel: (file) => {
					return {
						id: workout.id,
						uploadedAvatar: file ? [file] : undefined
					};
				},
				updateSharedData: (file, sharedData) => {
					if (sharedData.data.workout) {
						sharedData.data.workout.avatar = file ? [file] : [];
					}

					return sharedData;
				},
				customSave: (file) => {
					const trainingContainers = user.training?.containers ?? [];

					for (const container of trainingContainers) {
						for (const containerItem of container.items) {
							if (containerItem.id === data.workoutItem?.id) {
								containerItem.workout.avatar = file ? [file] : [];
							}
						}
					}

					return dependencies.httpService
						.post<UserDb.IUserItem, UserApi.IEditTrainingPlanRequest>((builder) =>
							builder
								.withCustomApiRoute('user', 'editUserTraining')
								.withId(user.id)
								.withData({
									title: user.training?.title ?? 'default',
									trainingContainers: trainingContainers
								})
								.build()
						)
						.pipe(map((response) => {}));
				}
			})
		};
	} else if (diet) {
		cardAvatar = {
			avatar: diet.avatar?.[0]?.image,
			avatarName: diet.system.name,
			upload: dependencies.coreAppService.getAvatarCardUploadConfig<
				DietDb.IDietItem,
				IViewOrEditDietPlanRouteData
			>({
				type: 'diet',
				existingAvatar: diet?.avatar?.[0],
				patchModel: (file) => {
					return {
						id: diet.id,
						uploadedAvatar: file ? [file] : undefined
					};
				},
				updateSharedData: (file, sharedData) => {
					if (sharedData.data.diet) {
						sharedData.data.diet.avatar = file ? [file] : [];
					}

					return sharedData;
				},
				customSave: (file) => {
					const nutritionContainers = user.nutrition?.containers ?? [];

					for (const container of nutritionContainers) {
						for (const containerItem of container.items) {
							if (containerItem.id === data.dietItem?.id) {
								containerItem.diet.avatar = file ? [file] : [];
							}
						}
					}

					return dependencies.httpService
						.post<UserDb.IUserItem, UserApi.IEditDietPlanRequest>((builder) =>
							builder
								.withCustomApiRoute('user', 'editUserDiet')
								.withId(user.id)
								.withData({
									title: user.nutrition?.title ?? 'default',
									dietContainers: nutritionContainers
								})
								.build()
						)
						.pipe(map((response) => {}));
				}
			})
		};
	} else {
		cardAvatar = {
			avatar: user.avatar,
			avatarName: user.email,
			upload: dependencies.coreAppService.getAvatarCardUploadConfig<UserDb.IUserItem, UserDb.IUserItem>({
				type: 'user',
				existingAvatar: user?.uploadedAvatar ? user.uploadedAvatar[0] : undefined,
				patchModel: (file) => {
					return {
						id: user.id,
						uploadedAvatar: file ? [file] : undefined
					};
				},
				updateSharedData: (file, sharedData) => {
					if (sharedData.data) {
						sharedData.data.uploadedAvatar = file ? [file] : [];
						sharedData.data.avatar = file?.image;
					}

					return sharedData;
				}
			})
		};
	}

	if (data.overrideCardSubtitle) {
		subtitle = data.overrideCardSubtitle;
	}

	if (data.overrideCardTitle) {
		title = data.overrideCardTitle;
	}

	const pdfButton: IButton | undefined = match({ workout, diet })
		.returnType<IButton | undefined>()
		.with({ workout: P.nonNullable }, (dataWithWorkout) => {
			if (dataWithWorkout.workout.type !== 'workout') {
				return undefined;
			}
			return {
				title: 'Download workout PDF',
				icon: 'pdf',
				click: () => {
					dependencies.dialogService.openPdfDialog({
						generatePdf: () =>
							dependencies.httpService.generateWorkoutPdf({
								workout: dataWithWorkout.workout,
								userId: user.id,
								containerItemId: data.workoutItem?.id
							})
					});
				}
			};
		})
		.with({ diet: P.nonNullable }, (dataWithDiet) => {
			return {
				icon: 'pdf',
				title: 'Download diet PDF',
				click: () => {
					dependencies.dialogService.openPdfDialog({
						generatePdf: () =>
							dependencies.httpService.generateDietPdf({
								diet: dataWithDiet.diet,
								userId: user.id,
								containerItemId: data.dietItem?.id
							})
					});
				}
			};
		})
		.otherwise(() => undefined);

	return {
		title: {
			value: title
		},
		avatar: cardAvatar,
		subtitle: subtitle,
		tags: tags,
		updated: user.system.updated,
		nutrition: diet
			? foodHelper.calculateFoodNutrientsForDiet(diet, data.linkedItems as FoodDb.IFoodItem[])
			: undefined,
		buttons: [...extraButtons, pdfButton].filter(isNotUndefined)
	};
}
